/* Descarga del catálogo de un proveedor Xtream sin perder nada en silencio. Antes cada lista (canales, películas, series) se pedía
   con un «si falla, vacía» y las seis a la vez: si el proveedor contestaba mal a una (con las conexiones llenas, o con la red del móvil)
   el catálogo se guardaba SIN esas películas y se quedaba así hasta la siguiente actualización. Ahora cada lista se reintenta, las listas
   no se piden todas a la vez, y lo que no se consiga se marca como parcial para volver a intentarlo enseguida y, mientras tanto, se
   conserva lo que ya había guardado de esa lista. */

export const PARTIAL_RETRY_MS = 3 * 60000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Pide una lista con reintentos. Una respuesta que no sea una lista (un mensaje de error del panel) cuenta como fallo; una lista vacía, no. */
export async function fetchList(fn, { attempts = 3, wait = sleep, backoff = 1500 } = {}) {
    let error = null;
    for (let i = 0; i < attempts; i++) {
        try {
            const v = await fn();
            if (Array.isArray(v)) return { ok: true, value: v };
            error = new Error('respuesta-no-valida');
        } catch (e) {
            error = e;
        }
        if (i < attempts - 1) await wait(backoff * (i + 1) * (i + 1));
    }
    return { ok: false, value: [], error };
}

/**
 * Baja canales, películas y series de un panel Xtream: de un tipo en un tipo (con su lista y sus categorías a la vez) para no saturar
 * al proveedor. Devuelve { live, vod, series, categories, partial: [tipos que no se pudieron bajar] }.
 */
export async function fetchXtreamLists(c, opts = {}) {
    const plan = [
        ['live', () => c.liveStreams(), () => c.liveCategories()],
        ['vod', () => c.vodStreams(), () => c.vodCategories()],
        ['series', () => c.series(), () => c.seriesCategories()]
    ];
    const out = { live: [], vod: [], series: [], categories: { live: [], vod: [], series: [] }, partial: [] };
    for (const [kind, list, cats] of plan) {
        const [l, k] = await Promise.all([fetchList(list, opts), fetchList(cats, opts)]);
        out[kind] = l.value;
        out.categories[kind] = k.value;
        if (!l.ok || !k.ok) out.partial.push(kind);
    }
    return out;
}

/** Lo que falló se recupera de la copia anterior (si la había); solo queda como parcial lo que tampoco estaba. */
export function restorePartial(fresh, prev) {
    if (!fresh || !fresh.partial || !fresh.partial.length) return fresh;
    const left = [];
    fresh.partial.forEach((kind) => {
        if (prev && Array.isArray(prev[kind]) && prev[kind].length) {
            fresh[kind] = prev[kind];
            if (prev.categories && prev.categories[kind]) fresh.categories[kind] = prev.categories[kind];
        } else left.push(kind);
    });
    fresh.partial = left;
    return fresh;
}

/** ¿El catálogo guardado se puede usar sin volver a pedirlo? Uno parcial se reintenta a los pocos minutos, no al cabo de horas. */
export function isFresh(data, ttl, now = Date.now()) {
    if (!data) return false;
    const age = now - (data.updatedAt || 0);
    return age < (data.partial && data.partial.length ? PARTIAL_RETRY_MS : ttl);
}
